import { SelectOption } from '@factorialco/deprecated-design-system'
import _ from 'lodash'
import moment from 'moment'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import type ContractVersion from 'modules/contracts/store/models/ContractVersion'
import type Employee from 'modules/employees/store/models/Employee'

const sortContractVersionsByEffectiveOn = (models: ContractVersion[]) => {
  return _.sortBy(models, (cv: ContractVersion) => cv.effectiveOn)
}

// Returns the subset of `models` (which should be the contract versions for
// an employee) that were effective (aka "current") between `startDate` and
// `endDate`.
const filterContractVersionsByCurrentWithinDateRange = (
  models: ContractVersion[],
  startDate: moment.Moment,
  endDate: moment.Moment
): ContractVersion[] => {
  const sortedModels = sortContractVersionsByEffectiveOn(models)

  return sortedModels.filter(
    (model, index) =>
      model.effectiveOn.isSameOrBefore(endDate, 'day') &&
      (index === sortedModels.length - 1 || // last one?
        sortedModels[index + 1]?.effectiveOn.isAfter(startDate, 'day'))
  )
}

export const filterContractVersionsByEmployee = (
  contractVersions: ContractVersions,
  employee: Employee
) => {
  return contractVersions.filter({
    employee_id: employee.id,
  })
}

const referenceContract = (
  models: ContractVersion[],
  date: moment.Moment
): ContractVersion | null | undefined => {
  const currentVersions = filterContractVersionsByCurrent(models, date)
  const currentNotTerminated = currentVersions.filter((m) => !m.terminatedOn(date))[0]

  if (currentNotTerminated) return currentNotTerminated

  const upcoming = getUpcomingContractVersions(models, date)

  if (upcoming) return upcoming

  return currentVersions[0]
}

// Returns the subset of `models` (which should be the contract versions for
// an employee) that were effective on the specified `date`. This subset will
// contain 1 contract version at the most.
export const filterContractVersionsByCurrent = (
  models: ContractVersion[],
  date: moment.Moment
): ContractVersion[] => {
  return filterContractVersionsByCurrentWithinDateRange(models, date, date)
}

export const getUpcomingContractVersions = (
  models: ContractVersion[],
  date: moment.Moment
): ContractVersion | null | undefined => {
  const future = models.filter((cv) => cv.effectiveOn.isAfter(date))

  return sortContractVersionsByEffectiveOn(future)[0]
}

export const getPreviousContractVersions = (
  models: ContractVersion[],
  contractVersion: ContractVersion
): ContractVersion | null | undefined => {
  const sortedColl = sortContractVersionsByEffectiveOn(models)

  const currentIndex = sortedColl.findIndex(
    (cv) => cv.attributes.id === contractVersion.attributes.id
  )

  return sortedColl[currentIndex - 1]
}

export const employeeContractVersions = (
  contractVersions: ContractVersions,
  employee: Employee
) => {
  return contractVersions.filter({
    employee_id: employee.attributes.id,
  })
}

export const employeeReferenceContract = (
  contractVersions: ContractVersions,
  employee: Employee
) => {
  return referenceContract(employeeContractVersions(contractVersions, employee), moment())
}

export const employeeHireDate = (contractVersions: ContractVersions, employee: Employee) => {
  return employeeReferenceContract(contractVersions, employee)?.attributes.starts_on
}

export const employeeIsFuture = (contractVersions: ContractVersions, employee: Employee) => {
  return (
    employeeReferenceContract(contractVersions, employee)?.startsOn?.isAfter(moment(), 'day') ??
    false
  )
}

export const employeeJobTitle = (contractVersions: ContractVersions, employee: Employee) => {
  return employeeReferenceContract(contractVersions, employee)?.attributes.job_title
}

export const currentContractVersionsWithinDateRange = (
  contractVersions: ContractVersions,
  startDate: moment.Moment,
  endDate: moment.Moment
) => {
  return _.chain(contractVersions.models)
    .groupBy((c) => c.attributes.employee_id)
    .map((groupedByEmployee) =>
      filterContractVersionsByCurrentWithinDateRange(groupedByEmployee, startDate, endDate)
    )
    .flatten()
    .value()
}

export const currentContractVersions = (contractVersions: ContractVersions) => {
  const date = moment()
  return currentContractVersionsWithinDateRange(contractVersions, date, date)
}

const labelMap: Record<string, string> = {
  contracts_fixed_amount_strategy: 'Fixed',
  contracts_variable_amount_strategy: 'Variable',
}

export const createSelectOptions = <T extends string>(
  values: readonly (T | { value: T; label: string })[]
): SelectOption[] =>
  values.map((item) =>
    typeof item === 'string'
      ? { label: labelMap[item] || item.charAt(0).toUpperCase() + item.slice(1), value: item }
      : { label: item.label, value: item.value }
  )
